import { useCallback, useRef, useState } from 'react';
import {
  mediaApi,
  uploadMedia,
  MAX_FILE_SIZE_BYTES,
  type MediaScope,
  type UploadResult,
} from '@entities/media';
import { ApiError } from '@shared/api';

export interface UploadItem {
  id: string;
  file: File;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
  result?: UploadResult;
}

interface UseMediaUploadsOptions {
  scope: MediaScope;
  accept?: string[];
  onUploaded: (result: UploadResult) => void;
}

function localId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useMediaUploads({ scope, accept, onUploaded }: UseMediaUploadsOptions) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const controllers = useRef<Map<string, AbortController>>(new Map());

  const updateItem = useCallback(
    (id: string, patch: Partial<UploadItem>) =>
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it))),
    [],
  );

  const removeItem = useCallback(
    (id: string) => {
      controllers.current.get(id)?.abort();
      controllers.current.delete(id);
      setItems((prev) => {
        const found = prev.find((it) => it.id === id);
        // If the file was already uploaded but the user dismisses it before
        // it gets persisted to an entity, clean up the orphan via DELETE.
        const mediaId = found?.result?.mediaId;
        if (mediaId) {
          mediaApi.remove(mediaId).catch((err) => {
            if (err instanceof ApiError && err.status === 404) return;
            console.warn('[media] remove failed', err);
          });
        }
        return prev.filter((it) => it.id !== id);
      });
    },
    [],
  );

  const validate = useCallback(
    (file: File): string | null => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        return `File exceeds ${Math.round(MAX_FILE_SIZE_BYTES / 1024 / 1024)} MB limit`;
      }
      if (accept && accept.length > 0 && !accept.includes(file.type)) {
        return `File type "${file.type || 'unknown'}" is not allowed`;
      }
      return null;
    },
    [accept],
  );

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const list = Array.from(files);
      for (const file of list) {
        const id = localId();
        const validationError = validate(file);
        if (validationError) {
          setItems((prev) => [
            ...prev,
            { id, file, progress: 0, status: 'error', error: validationError },
          ]);
          continue;
        }

        const controller = new AbortController();
        controllers.current.set(id, controller);
        setItems((prev) => [...prev, { id, file, progress: 0, status: 'uploading' }]);

        uploadMedia({
          scope,
          file,
          signal: controller.signal,
          onProgress: ({ loaded, total }) =>
            updateItem(id, { progress: total > 0 ? Math.round((loaded / total) * 100) : 0 }),
        })
          .then((result) => {
            updateItem(id, { status: 'done', progress: 100, result });
            onUploaded(result);
          })
          .catch((err: unknown) => {
            const message = err instanceof Error ? err.message : 'Upload failed';
            updateItem(id, { status: 'error', error: message });
          })
          .finally(() => controllers.current.delete(id));
      }
    },
    [scope, validate, updateItem, onUploaded],
  );

  return { items, addFiles, removeItem };
}
